import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  DomainResultRow,
  EmptyState,
  ListGroup,
  ListRow,
  SearchField,
  SegmentedControl,
  Skeleton,
  TextField,
} from '@uplink/ui';
import { useEffect, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { haptic } from '../telegram.ts';

type Zone = 'popular' | 'all';

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function DomainsScreen({ nav, initialQuery }: { nav: Nav; initialQuery?: string }) {
  const trpc = useTRPC();
  const { t, money, date } = usePrefs();
  const [query, setQuery] = useState(initialQuery ?? '');
  const [zone, setZone] = useState<Zone>('popular');
  const typed = query.trim();
  const q = useDebounced(typed, 350);

  const search = useQuery({
    // A newer query cancels the older one, so the registrar's 1-request-per-second queue doesn't back up
    ...trpc.domains.search.queryOptions({ query: q, zone }, { trpc: { abortOnUnmount: true } }),
    enabled: q.length >= 1,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  const mine = useQuery(trpc.domains.list.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const [aiOpen, setAiOpen] = useState(false);
  // From the first keystroke until the answer for exactly this text is in
  const checking = typed !== '' && (typed !== q || search.isFetching);
  const stale = checking && !!search.data;

  return (
    <main className={aiOpen && !typed ? 'ul-screen ul-screen--with-action' : 'ul-screen'}>
      <h1 className="ul-screen__title">{t.domains.title}</h1>
      <SearchField
        id="domain-query"
        placeholder={t.domains.searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <SegmentedControl<Zone>
        label={t.domains.zones}
        value={zone}
        onChange={(z) => {
          haptic.select();
          setZone(z);
        }}
        options={[
          { value: 'popular', label: t.domains.popular },
          { value: 'all', label: t.domains.all },
        ]}
      />

      {typed ? (
        <div className="ul-search-status" aria-live="polite">
          {checking ? (
            <>
              <span className="ul-sig ul-sig--provisioning" aria-hidden>
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="ul-sig__bar is-on"
                    style={{ height: 4 + i * 3, animationDelay: `${i * 120}ms` }}
                  />
                ))}
              </span>
              {t.domains.checking}
            </>
          ) : null}
        </div>
      ) : null}

      {typed ? (
        search.isError && !checking ? (
          <div className="ul-error" role="alert">
            {errorText(search.error)}
          </div>
        ) : !search.data ? (
          <div className="ul-stack" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={56} />
            ))}
          </div>
        ) : search.data.length === 0 ? (
          <EmptyState icon="search" title={t.domains.nothingTitle} text={t.domains.nothingText} />
        ) : (
          <div className={stale ? 'ul-results ul-results--stale' : 'ul-results'}>
            <ListGroup header={t.domains.results} footer={t.domains.resultsFooter}>
              {search.data.map((r) => (
                <DomainResultRow
                  key={r.domain}
                  label={r.label}
                  tld={r.tld}
                  status={r.status}
                  price={r.price === null ? null : money(r.price)}
                  renew={r.renew === null ? null : money(r.renew)}
                  onSelect={
                    r.status === 'available' && r.price !== null
                      ? () =>
                          nav.push({
                            name: 'domainCheckout',
                            domain: r.domain,
                            label: r.label,
                            tld: r.tld,
                            price: r.price!,
                            renew: r.renew,
                          })
                      : undefined
                  }
                />
              ))}
            </ListGroup>
          </div>
        )
      ) : null}

      {!typed && config.data?.aiIdeas ? (
        <AiIdeas nav={nav} open={aiOpen} onOpen={() => setAiOpen(true)} />
      ) : null}

      {typed ? null : mine.data && mine.data.length > 0 ? (
        <ListGroup header={t.domains.mine}>
          {mine.data.map((d) => (
            <ListRow
              key={d.id}
              icon="globe"
              title={d.name}
              subtitle={
                d.status === 'active'
                  ? `${t.common.until(d.expiresAt ? date(d.expiresAt) : '—')}${d.autoRenew ? ` · ${t.domains.autoRenewOn}` : ''}`
                  : d.status === 'expired'
                    ? t.domains.expiredRenew
                    : t.domains.registering
              }
              chevron
              onClick={() => nav.push({ name: 'domain', id: d.id })}
            />
          ))}
        </ListGroup>
      ) : mine.isPending ? null : (
        <EmptyState icon="globe" title={t.domains.emptyTitle} text={t.domains.emptyText} />
      )}
    </main>
  );
}

/**
 * "Pick a name with AI": the user describes the project, the API asks the model for names and returns
 * only the ones the registrar says are free, with our prices.
 */
function AiIdeas({ nav, open, onOpen }: { nav: Nav; open: boolean; onOpen: () => void }) {
  const trpc = useTRPC();
  const { t, money } = usePrefs();
  const [description, setDescription] = useState('');
  const ideas = useMutation(
    trpc.domains.ideas.mutationOptions({
      onSuccess: () => haptic.success(),
      onError: () => haptic.error(),
    }),
  );
  const text = description.trim();

  if (!open) {
    return (
      <ListGroup>
        <ListRow
          icon="ai"
          title={t.domains.aiRow}
          subtitle={t.domains.aiRowText}
          chevron
          onClick={() => {
            haptic.select();
            onOpen();
          }}
        />
      </ListGroup>
    );
  }

  return (
    <section className="ul-stack">
      <div className="ul-group__header">{t.domains.aiRow}</div>
      <TextField
        id="ai-description"
        label={t.domains.aiLabel}
        value={description}
        onChange={setDescription}
        placeholder={t.domains.aiPlaceholder}
        hint={t.domains.aiHint}
      />
      {ideas.isError ? (
        <div className="ul-error" role="alert">
          {errorText(ideas.error)}
        </div>
      ) : null}
      {ideas.isPending ? (
        <div className="ul-stack" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={56} />
          ))}
        </div>
      ) : ideas.data ? (
        ideas.data.length === 0 ? (
          <EmptyState icon="ai" title={t.domains.aiNothingTitle} text={t.domains.aiNothingText} />
        ) : (
          <ListGroup header={t.domains.aiResults} footer={t.domains.aiFooter}>
            {ideas.data.map((r) => (
              <DomainResultRow
                key={r.domain}
                label={r.label}
                tld={r.tld}
                status={r.status}
                price={r.price === null ? null : money(r.price)}
                renew={r.renew === null ? null : money(r.renew)}
                onSelect={
                  r.price !== null
                    ? () =>
                        nav.push({
                          name: 'domainCheckout',
                          domain: r.domain,
                          label: r.label,
                          tld: r.tld,
                          price: r.price!,
                          renew: r.renew,
                        })
                    : undefined
                }
              />
            ))}
          </ListGroup>
        )
      ) : null}
      <MainActionBar
        action={{
          text: ideas.data ? t.domains.aiAgain : t.domains.aiPick,
          loading: ideas.isPending,
          disabled: text.length < 3,
          onClick: () => ideas.mutate({ description: text }),
        }}
      />
    </section>
  );
}
